import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { ClickableImage, ID_PLACEHOLDER, SELFIE_PLACEHOLDER } from '../ImagePreview';
import { FileImage, Loader2, ShieldCheck, ShieldOff, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Document viewer for staff (admin/moderator/cro/mro).
 * Admins additionally get inline "Replace" upload controls for each document
 * (ID photo, selfie/profile photo, business permit for merchants).
 *
 * Re-uploading just swaps the URL — it does NOT auto-trigger OCR or change
 * verification_status. Admin manages verification separately.
 */
export function UserDocsDialog({ open, onOpenChange, user }) {
  const { api, user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  const fetchDocs = async () => {
    if (!user?.id) return;
    setLoading(true);
    setErr('');
    try {
      const res = await api.get(`/api/staff/documents/${user.id}`);
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load documents');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!open) return;
    setData(null);
    fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  const docs = data?.documents || {};
  const isMerchant = data?.user?.role === 'merchant';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#041A3B] border-white/10 max-w-2xl"
        data-testid="user-docs-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FileImage className="w-5 h-5 text-[#00E5FF]" />
            {user?.name ? `${user.name}'s Documents` : 'User Documents'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#00E5FF]" />
            <p className="text-[#A0B4CC] text-sm mt-2">Loading documents…</p>
          </div>
        ) : err ? (
          <div className="py-8 text-center text-[#FF1744] text-sm" data-testid="docs-error">{err}</div>
        ) : (
          <div className="space-y-4">
            {/* Verification meta */}
            <div className="flex items-center gap-2 text-xs flex-wrap">
              {data?.user?.verification_status === 'verified' ? (
                <span className="px-2 py-1 rounded bg-[#00E676]/10 text-[#00E676] flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> Verified
                </span>
              ) : (
                <span className="px-2 py-1 rounded bg-[#FFEA00]/10 text-[#FFEA00] flex items-center gap-1">
                  <ShieldOff className="w-3 h-3" /> {data?.user?.verification_status || 'Unverified'}
                </span>
              )}
              {docs.id_name_extracted && (
                <span className="text-[#5C7599]">OCR name: <span className="text-white">{docs.id_name_extracted}</span></span>
              )}
              {typeof docs.ocr_confidence === 'number' && (
                <span className="text-[#5C7599]">Confidence: <span className="text-white">{(docs.ocr_confidence * 100).toFixed(0)}%</span></span>
              )}
              {isAdmin && (
                <span className="ml-auto text-[10px] text-[#5C7599] italic">
                  Replacing a file does not change verification status.
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DocumentSlot
                label="Government ID"
                src={docs.id_photo_url}
                fallbackSrc={ID_PLACEHOLDER}
                previewLabel={`ID Photo — ${data?.user?.name || ''}`}
                api={api}
                isAdmin={isAdmin}
                fieldName="id_photo_url"
                userId={user?.id}
                userName={data?.user?.name || ''}
                onUpdated={fetchDocs}
                testId="user-docs-id-photo"
              />
              <DocumentSlot
                label="Selfie / Profile Photo"
                src={docs.selfie_url}
                fallbackSrc={SELFIE_PLACEHOLDER}
                previewLabel={`Selfie — ${data?.user?.name || ''}`}
                api={api}
                isAdmin={isAdmin}
                fieldName="selfie_url"
                userId={user?.id}
                userName={data?.user?.name || ''}
                onUpdated={fetchDocs}
                testId="user-docs-selfie"
              />
              {(isMerchant || docs.business_permit_url) && (
                <div className="sm:col-span-2">
                  <DocumentSlot
                    label="Business Permit"
                    src={docs.business_permit_url}
                    previewLabel={`Business Permit — ${data?.user?.name || ''}`}
                    api={api}
                    isAdmin={isAdmin && isMerchant}
                    fieldName="business_permit_url"
                    userId={user?.id}
                    userName={data?.user?.merchant_info?.business_name || data?.user?.name || ''}
                    onUpdated={fetchDocs}
                    testId="user-docs-permit"
                    tall
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DocumentSlot({
  label, src, fallbackSrc, previewLabel, api, isAdmin,
  fieldName, userId, userName = '', onUpdated, testId, tall = false,
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = () => fileRef.current?.click();

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Max 10MB');
      return;
    }
    setUploading(true);
    try {
      // Map the field being replaced to a purpose tag for the storage filename.
      const purposeMap = {
        id_photo_url: 'id-photo',
        selfie_url: 'selfie',
        id_image_url: 'id-photo',
        selfie_image_url: 'selfie',
        business_permit_url: 'business-permit',
        business_logo_url: 'merchant-logo',
        profile_photo_url: 'profile-pic',
      };
      // 1) Upload binary → get permanent /api/files/{id} URL
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', purposeMap[fieldName] || 'attachment');
      if (userName) fd.append('subject_name', userName);
      const up = await api.post('/api/upload/image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newUrl = up.data.url;

      // 2) Patch the user record with the new URL
      await api.put(`/api/admin/users/${userId}/documents`, { [fieldName]: newUrl });
      toast.success(`${label} replaced`);
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to replace ${label.toLowerCase()}`);
    }
    setUploading(false);
  };

  return (
    <div data-testid={`${testId}-slot`}>
      <p className="text-[#5C7599] text-xs mb-1.5">{label}</p>
      <ClickableImage
        src={src}
        fallbackSrc={fallbackSrc}
        label={previewLabel}
        alt={label}
        className={`w-full ${tall ? 'h-56' : 'h-48'} object-cover rounded-lg border border-white/10`}
        data-testid={testId}
      />
      {isAdmin && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleUpload}
            data-testid={`${testId}-input`}
          />
          <Button
            type="button"
            onClick={handlePick}
            disabled={uploading}
            variant="outline"
            size="sm"
            className="mt-2 w-full border-[#00E5FF]/30 text-[#00E5FF] hover:bg-[#00E5FF]/10 hover:text-[#00E5FF]"
            data-testid={`${testId}-replace-btn`}
          >
            {uploading ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Uploading…</>
            ) : src ? (
              <><ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Replace</>
            ) : (
              <><Upload className="w-3.5 h-3.5 mr-1.5" /> Upload</>
            )}
          </Button>
        </>
      )}
    </div>
  );
}
